"use client";

import { useState, useEffect, useCallback } from "react";
import Image from "next/image";
import { cn } from "@/lib/utils";

export interface PartsCarouselProps {
	/** 轮播标题 */
	title: string;
	/** 图片路径数组 */
	images: string[];
	/** 图片 alt 文字前缀 */
	altPrefix?: string;
	/** 自动轮播间隔（毫秒），默认 4000ms，设为 0 禁用自动轮播 */
	autoPlayInterval?: number;
	/** 移动端每次滚动的百分比，默认 50%（显示2张） */
	mobileScrollPercent?: number;
	/** 桌面端每次滚动的百分比，默认 25%（显示4张） */
	desktopScrollPercent?: number;
	/** 移动端总页数，默认根据图片数量自动计算 */
	mobilePageCount?: number;
	/** 桌面端总页数，默认根据图片数量自动计算 */
	desktopPageCount?: number;
	/** 图片背景色，默认 #bfbfbf */
	imageBgColor?: string;
	className?: string;
}

const PartsCarousel = ({
	title,
	images,
	altPrefix = "Part",
	autoPlayInterval = 4000,
	mobileScrollPercent = 50,
	desktopScrollPercent = 25,
	mobilePageCount,
	desktopPageCount,
	imageBgColor = "#bfbfbf",
	className,
}: PartsCarouselProps) => {
	const [currentIndex, setCurrentIndex] = useState(0);
	const [isMobile, setIsMobile] = useState(false);

	// 监听屏幕尺寸变化，判断是否为移动端
	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(window.innerWidth < 768);
		};
		
		// 初始化检测
		checkMobile();
		
		// 监听窗口大小变化
		window.addEventListener("resize", checkMobile);
		return () => window.removeEventListener("resize", checkMobile);
	}, []);

	// 根据设备类型计算当前滚动百分比
	const scrollPercent = isMobile ? mobileScrollPercent : desktopScrollPercent;

	// 根据设备类型计算页数（移动端显示2张，桌面端显示4张）
	const mobilePages = mobilePageCount ?? Math.ceil(images.length / 2);
	const desktopPages = desktopPageCount ?? Math.ceil(images.length / 4);
	const pageCount = isMobile ? mobilePages : desktopPages;

	// 当切换设备类型时，重置 index 避免越界
	useEffect(() => {
		if (currentIndex >= pageCount) {
			setCurrentIndex(0);
		}
	}, [pageCount, currentIndex]);

	const nextSlide = useCallback(() => {
		setCurrentIndex((prev) => (prev + 1) % pageCount);
	}, [pageCount]);

	useEffect(() => {
		if (autoPlayInterval <= 0 || pageCount <= 1) return;
		const timer = setInterval(nextSlide, autoPlayInterval);
		return () => clearInterval(timer);
	}, [nextSlide, autoPlayInterval, pageCount]);

	return (
		<section className={cn("bg-white py-8 sm:py-10 md:py-12 lg:py-16", className)}>
		<div className="container">
			<h3 className="mb-6 text-lg font-bold text-black sm:mb-8 sm:text-xl md:mb-10 md:text-2xl lg:mb-12">
				{title}
			</h3>

			{/* 图片轮播 - 横向滚动 */}
			<div className="relative overflow-hidden">
				<div
					className="flex transition-transform duration-500 ease-in-out"
					style={{ transform: `translateX(-${currentIndex * scrollPercent}%)` }}
				>
					{images.map((src, index) => (
						<div
							key={index}
							className="w-1/2 flex-shrink-0 px-2 md:w-1/4 md:px-3"
						>
							<div
								className="relative aspect-[4/3] w-full overflow-hidden"
								style={{ backgroundColor: imageBgColor }}
							>
								<Image
									src={src}
									alt={`${altPrefix} ${index + 1}`}
									fill
									className="object-contain"
									sizes="(max-width: 768px) 50vw, 25vw"
								/>
							</div>
						</div>
					))}
				</div>
			</div>

			{/* 分页圆点 */}
			{pageCount > 1 && (
				<div className="mt-6 flex items-center justify-center gap-2 md:mt-8">
					{Array.from({ length: pageCount }, (_, index) => (
						<button
							key={index}
							onClick={() => setCurrentIndex(index)}
							className={`h-2.5 w-2.5 rounded-full transition-colors duration-300 ${
								currentIndex === index ? "bg-[#9acd32]" : "bg-[#c8e69c]"
							}`}
							aria-label={`Go to page ${index + 1}`}
						/>
					))}
				</div>
			)}
		</div>
		</section>
	);
};

export default PartsCarousel;
